import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchRinascenteConfig,
  updateRinascenteConfidence,
  fetchRinascenteConfidencePreview,
} from "../../api/client";
import type { RinascenteConfig, RinascenteConfidencePreview } from "../../api/types";
import { useToast } from "../../context/ToastContext";

type TabId = "confidence" | "output" | "extraction" | "input";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "confidence", label: "Confidence", icon: "tune" },
  { id: "output", label: "Output", icon: "output" },
  { id: "extraction", label: "Estrazione", icon: "auto_fix_high" },
  { id: "input", label: "Input", icon: "input" },
];

export function RinascenteSettings() {
  const { addToast } = useToast();
  const [config, setConfig] = useState<RinascenteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("confidence");

  const load = useCallback(() => {
    setLoading(true);
    fetchRinascenteConfig()
      .then((c) => { setConfig(c); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
          <div className="spinner spinner-lg" />
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="page-container">
        <div className="card" style={{ padding: "var(--space-6)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ verticalAlign: "middle", marginRight: 8 }}>error</span>
          Errore caricamento configurazione: {error || "Config non trovata"}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">
        <span className="material-symbols-outlined" style={{ fontSize: 28, verticalAlign: "middle", marginRight: 8 }}>tune</span>
        Configurazione Rinascente
      </h1>

      {/* Tabs */}
      <div className="flow-tab-bar" style={{ marginBottom: "var(--space-4)" }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`flow-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "confidence" && (
        <ConfidenceTab config={config} onReload={load} addToast={addToast} />
      )}
      {activeTab === "output" && <OutputTab config={config} />}
      {activeTab === "extraction" && <ExtractionTab config={config} />}
      {activeTab === "input" && <InputTab config={config} />}
    </div>
  );
}

// ── CONFIDENCE TAB ────────────────────────────────────────────────────────────

function ConfidenceTab({
  config, onReload, addToast,
}: {
  config: RinascenteConfig;
  onReload: () => void;
  addToast: (type: "success" | "error" | "info", msg: string) => void;
}) {
  const [threshold, setThreshold] = useState(config.confidence_threshold_auto_approve);
  const [origThreshold, setOrigThreshold] = useState(config.confidence_threshold_auto_approve);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<RinascenteConfidencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dirty = threshold !== origThreshold;
  const thresholdDisplay = threshold >= 999 ? "Spento" : threshold.toFixed(2);

  // Preview live con debounce
  useEffect(() => {
    if (threshold >= 999 || !dirty) {
      setPreview(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewLoading(true);
      fetchRinascenteConfidencePreview(threshold, 30)
        .then((p) => setPreview(p))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [threshold, dirty]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRinascenteConfidence(threshold);
      setOrigThreshold(threshold);
      addToast("success", `Soglia auto-approvazione aggiornata: ${thresholdDisplay}`);
      onReload();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setThreshold(origThreshold);
  };

  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-md)" }}>Soglia auto-approvazione</h3>
      <p className="text-sm text-muted" style={{ margin: "0 0 var(--space-4)" }}>
        Le fatture con completeness ≥ soglia vengono approvate automaticamente senza intervento dell'operatore.
        Usa 999 (o "Spegni") per disattivare l'auto-approve e richiedere la revisione manuale di tutte le fatture.
      </p>

      {/* Slider */}
      <div className="flow-field" style={{ marginBottom: "var(--space-4)" }}>
        <label className="flow-label" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "var(--space-2)" }}>
          Soglia: <strong>{thresholdDisplay}</strong>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>0</span>
          <input
            type="range"
            className="flow-slider"
            min={0}
            max={1}
            step={0.01}
            value={threshold >= 999 ? 1 : threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>1.0</span>
          <button
            className={`btn-toggle-sm${threshold >= 999 ? " active" : ""}`}
            onClick={() => setThreshold(threshold >= 999 ? 0.85 : 999)}
            title={threshold >= 999 ? "Attiva auto-approve" : "Spegni auto-approve"}
          >
            {threshold >= 999 ? "Attiva" : "Spegni"}
          </button>
        </div>
      </div>

      {/* Preview impatto */}
      {threshold < 999 && dirty && (
        <div className="flow-preview-box" style={{
          background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
          padding: "var(--space-4)", marginTop: "var(--space-4)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-info)" }}>preview</span>
            <strong>Preview impatto</strong>
            {previewLoading && <div className="spinner spinner-sm" />}
          </div>
          {preview ? (
            <>
              <div style={{ fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)" }}>
                Su <strong>{preview.total}</strong> fatture (ultimi {preview.period_days} giorni):
              </div>
              <div style={{ display: "flex", gap: 2, height: 24, borderRadius: "var(--border-radius-sm)", overflow: "hidden", marginBottom: "var(--space-3)" }}>
                <div style={{
                  width: preview.total > 0 ? `${(preview.would_auto_approve / preview.total) * 100}%` : "0%",
                  background: "var(--indaco-success)",
                }} />
                <div style={{
                  width: preview.total > 0 ? `${(preview.would_need_review / preview.total) * 100}%` : "0%",
                  background: "var(--indaco-warning)",
                }} />
              </div>
              <div style={{ display: "flex", gap: "var(--space-6)", fontSize: "var(--font-size-sm)" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="preview-dot preview-dot-auto" />
                  Auto-approvate: <strong>{preview.would_auto_approve}</strong>{" "}
                  ({preview.total > 0 ? Math.round((preview.would_auto_approve / preview.total) * 100) : 0}%)
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="preview-dot preview-dot-manual" />
                  Revisione manuale: <strong>{preview.would_need_review}</strong>
                </span>
              </div>
            </>
          ) : !previewLoading ? (
            <div className="text-sm text-muted">Nessun dato disponibile per la preview</div>
          ) : null}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? <><div className="spinner spinner-sm" style={{ marginRight: 6, display: "inline-block", verticalAlign: "middle" }} />Salvataggio…</> : "Salva"}
        </button>
        <button className="btn btn-outline" onClick={handleCancel} disabled={!dirty || saving}>
          Annulla
        </button>
      </div>
    </div>
  );
}

// ── OUTPUT TAB (readonly per ora) ─────────────────────────────────────────────

function OutputTab({ config }: { config: RinascenteConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione output</h3>
      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", marginBottom: "var(--space-3)",
        display: "flex", alignItems: "center", gap: "var(--space-2)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>folder</span>
        <span>Directory output: <code>{config.output_dir}</code></span>
      </div>
      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", display: "flex", alignItems: "center", gap: "var(--space-2)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>description</span>
        <span>Formato: <strong>JSON</strong> (un file per fattura, nominato come il PDF originale con estensione <code>.json</code>)</span>
      </div>
      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        Il JSON viene scritto al momento dell'approvazione. È consultabile e scaricabile dalla pagina "Coda Output".
      </p>
    </div>
  );
}

// ── EXTRACTION TAB (readonly — prompt template visibile) ──────────────────────

function ExtractionTab({ config }: { config: RinascenteConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione estrazione</h3>

      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", marginBottom: "var(--space-3)",
      }}>
        <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>Pipeline</div>
        <div className="text-sm">
          OCR <strong>Mistral mistral-ocr-4-0</strong> + grafo LangGraph con estrazione strutturata via <strong>Claude Sonnet</strong>.
          Chunking automatico per PDF &gt; 3 pagine (4 pagine per chunk, dedup righe per codice articolo).
        </div>
      </div>

      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", marginBottom: "var(--space-3)",
        display: "flex", alignItems: "center", gap: "var(--space-2)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>memory</span>
        <span>Limite OCR: <strong>{(config.ocr_max_pdf_bytes / (1024 * 1024)).toFixed(0)} MB</strong> per PDF</span>
      </div>

      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)",
      }}>
        <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>Metrica completeness</div>
        <div className="text-sm text-muted">
          La completeness (0-1) è la frazione dei 9 campi critici estratti correttamente (numero, data, fornitore, cliente, totali, righe, ecc.).
          È usata come proxy di confidence per l'auto-approvazione e per i filtri nella dashboard.
        </div>
      </div>

      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        I prompt template di estrazione sono in <code>src/rinascente/nodes/</code> e richiedono un deploy per essere modificati.
      </p>
    </div>
  );
}

// ── INPUT TAB (readonly) ──────────────────────────────────────────────────────

function InputTab({ config }: { config: RinascenteConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione input</h3>

      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", marginBottom: "var(--space-3)",
        display: "flex", alignItems: "center", gap: "var(--space-2)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>folder_open</span>
        <span>Directory sorgente: <code>{config.source_dir}</code></span>
      </div>

      <div className="flow-readonly-card" style={{
        padding: "var(--space-3)", background: "var(--indaco-gray-50)",
        borderRadius: "var(--border-radius-md)", marginBottom: "var(--space-3)",
        display: "flex", alignItems: "center", gap: "var(--space-2)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>schedule</span>
        <span>Intervallo watcher: <strong>{config.watcher_interval_seconds}s</strong></span>
      </div>

      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        I PDF delle fatture estere vengono copiati manualmente nella directory sorgente (nessuna mailbox nel POC).
        Il watcher scansiona la directory ogni {config.watcher_interval_seconds} secondi e processa i nuovi PDF.
      </p>
    </div>
  );
}
